import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App as AntApp, Button, Input } from 'antd';
import AppBar from '../components/AppBar';
import AgentCard from '../components/AgentCard';
import useSheetAnim from '../hooks/useSheetAnim';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { AsyncButton, Chip, Skeletons, RM, dt } from '../components/kit';
import * as coShareApi from '../api/coShare';

/**
 * FULL-SCREEN · /co-share/:id — one co-share request, from whichever end you are on.
 *
 * The screen is the same record for both agents; only the verbs change.
 *
 *   LISTER (it's your listing)    → Accept (settling the split) · Decline · then
 *                                   Create sale, which is the whole point: the sale
 *                                   is raised on your listing with the other agent
 *                                   already on it as a co-agent.
 *   REQUESTER (you asked)         → wait, and Withdraw if the buyer falls away.
 *
 * Every verb the server would refuse is simply not offered — the payload says which
 * ones are live (can_respond / can_create_sale / can_withdraw), so this screen never
 * has to re-derive the rules.
 */

const TONE = { PENDING: 'warn', ACCEPTED: 'ok', REJECTED: 'bad', WITHDRAWN: 'muted', CONVERTED: 'brand' };

// The one line at the top that says where this stands, in the reader's own terms.
const HEADLINE = {
  LISTER: {
    PENDING: 'Waiting on you to answer.',
    ACCEPTED: 'You accepted. Raise the sale when the deal is done — the co-agent goes on it automatically.',
    REJECTED: 'You declined this request.',
    WITHDRAWN: 'This co-share was pulled. The listing is yours alone again.',
    CONVERTED: 'A sale was raised from this co-share.',
  },
  REQUESTER: {
    PENDING: 'Sent. Waiting on the listing agent — you’ll get a notification either way.',
    ACCEPTED: 'Accepted. The listing agent raises the sale; you are on it as a co-agent at the agreed split.',
    REJECTED: 'The listing agent declined this one.',
    WITHDRAWN: 'This co-share was pulled. You can send a fresh request on the listing.',
    CONVERTED: 'The sale is raised — open it to follow the deal.',
  },
};

export default function CoShareDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message, modal } = AntApp.useApp();
  const [req, setReq] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);

  const load = () =>
    coShareApi.getCoShareRequestById(id)
      .then((r) => setReq(r?.data ?? r))
      .catch((e) => message.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isLister = req?.role === 'LISTER';
  const other = isLister ? req?.requester : req?.lister;
  const listing = req?.listing || null;
  const split = req?.agreed_split_percentage ?? req?.proposed_split_percentage ?? null;

  const accept = async ({ agreed, note }) => {
    await coShareApi.acceptCoShareRequest(id, {
      agreed_split_percentage: agreed === '' || agreed === null ? null : Number(agreed),
      response_note: note || null,
    });
    setAcceptOpen(false);
    message.success('Co-share accepted');
    await load();
  };

  // Declining and withdrawing both end the request, so both confirm first and both
  // take a reason — the other agent is told what happened, and "no reason given" is
  // a worse message than a short one.
  const promptNote = ({ title, content, okText, danger, placeholder }, run) =>
    new Promise((resolve) => {
      let note = '';
      modal.confirm({
        centered: true,
        title,
        okText,
        cancelText: 'Keep it open',
        okButtonProps: danger ? { danger: true } : undefined,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 6 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{content}</div>
            <Input.TextArea
              maxLength={1000}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder={placeholder}
              onChange={(e) => { note = e.target.value; }}
            />
          </div>
        ),
        onCancel: () => resolve(),
        onOk: () => run(note.trim()).then(resolve, (e) => { message.error(e.message); resolve(); }),
      });
    });

  const reject = () =>
    promptNote(
      {
        title: 'Decline this co-share?',
        content: `${other?.name || 'The other agent'} is told straight away. They can ask again later if things change.`,
        okText: 'Decline',
        danger: true,
        placeholder: 'Why — already have an offer, buyer isn’t a fit…',
      },
      async (note) => {
        await coShareApi.rejectCoShareRequest(id, { response_note: note || null });
        message.success('Co-share declined');
        await load();
      }
    );

  const withdraw = () =>
    promptNote(
      {
        title: isLister ? 'Cancel this co-share?' : 'Withdraw your request?',
        content: isLister
          ? 'The other agent comes off the deal. Either of you can start a fresh request on this listing afterwards.'
          : 'The listing agent is told you have pulled out. You can send a new request on this listing later.',
        okText: isLister ? 'Cancel co-share' : 'Withdraw',
        danger: true,
        placeholder: 'What changed?',
      },
      async (note) => {
        await coShareApi.withdrawCoShareRequest(id, { response_note: note || null });
        message.success(isLister ? 'Co-share cancelled' : 'Request withdrawn');
        await load();
      }
    );

  /**
   * The payoff. The sale form opens prefilled from the listing AND from this
   * request: both agents in the split at the agreed percentages, the listing
   * linked, the property and price filled in. Saving it sends
   * `co_share_request_id`, which is what pins the requester into sale_agents
   * server-side and spends the request — the client never has to be trusted for
   * either.
   */
  const createSale = () =>
    nav('/sales/new', {
      state: {
        fromCoShare: {
          id: req.id,
          agreed_split_percentage: req.agreed_split_percentage,
          requester: req.requester,
          lister: req.lister,
          listing_id: listing?.id || null,
          listing_type: listing?.listing_type || null,
          property_address: listing?.property_address || null,
          asking_price_rm: listing?.asking_price_rm ?? null,
        },
      },
    });

  if (!req) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Co-share" />
        <div style={{ padding: 18 }}><Skeletons n={3} height={90} /></div>
      </div>
    );
  }

  const headline = HEADLINE[req.role]?.[req.status] || '';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title="Co-share request"
        sub={listing?.listing_ref || undefined}
        right={<Chip label={req.status} tone={TONE[req.status]} />}
      />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* where this stands */}
        <div style={{ background: req.status === 'PENDING' && isLister ? 'color-mix(in srgb,var(--warn) 10%,var(--surface))' : 'var(--surface)', border: `1px solid ${req.status === 'PENDING' && isLister ? 'color-mix(in srgb,var(--warn) 34%,var(--border))' : 'var(--border)'}`, borderRadius: 14, padding: '15px 16px', fontSize: 13, lineHeight: 1.55 }}>
          {headline}
        </div>

        {/* the property */}
        {listing && (
          <div
            onClick={() => nav(`/listings/${listing.id}`)}
            style={{ display: 'flex', gap: 14, padding: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer' }}
          >
            {listing.cover_image_url ? (
              <img src={listing.cover_image_url} alt="" style={{ width: 70, height: 70, borderRadius: 11, flex: 'none', objectFit: 'cover', border: '1px solid var(--border)' }} />
            ) : (
              <div style={{ width: 70, height: 70, borderRadius: 11, flex: 'none', background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 6px,var(--chip) 6px,var(--chip) 12px)', border: '1px solid var(--border)' }} />
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{listing.property_title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                <span style={{ fontFamily: 'ui-monospace,monospace' }}>{listing.listing_ref}</span>
                {' · '}{[listing.area, listing.state].filter(Boolean).join(', ') || '—'}
              </div>
              {/* The street address is withheld from the requester until the lister
                  accepts — same rule the co-broke listing view follows. */}
              {listing.property_address && (
                <div style={{ fontSize: 11.5, lineHeight: 1.4 }}>{listing.property_address}</div>
              )}
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
                {listing.asking_price_rm != null ? RM(listing.asking_price_rm) : '—'}
                {listing.listing_type === 'RENT' && <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--muted)' }}>/month</span>}
              </div>
            </div>
          </div>
        )}

        {/* who is on the other end */}
        <AgentCard
          agent={other}
          label={isLister ? 'REQUESTING AGENT' : 'LISTING AGENT'}
          note={isLister ? 'The agent asking to co-share this property.' : 'The agent holding this listing.'}
          waText={listing ? `Hi ${other?.name || ''}, about the co-share on ${listing.listing_ref}` : undefined}
        />

        {/* the terms */}
        <div data-tour="cs-terms" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '4px 14px' }}>
          <Row k="Requested" v={dt(req.created_at)} />
          <Row
            k={req.agreed_split_percentage != null ? 'Agreed split' : 'Proposed split'}
            v={split != null ? `${split}% to ${req.requester?.name || 'the requesting agent'}` : '—'}
            accent={split != null}
          />
          {req.responded_at && <Row k={req.status === 'REJECTED' ? 'Declined' : 'Answered'} v={dt(req.responded_at)} />}
          {req.withdrawn_at && <Row k="Withdrawn" v={dt(req.withdrawn_at)} />}
          <Row k="Sale" v={req.sale_ref || '—'} mono={!!req.sale_ref} accent={!!req.sale_ref} last />
        </div>

        {req.message && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>
              {isLister ? 'WHAT THEY SAID' : 'WHAT YOU SAID'}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{req.message}</div>
          </div>
        )}

        {req.response_note && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>
              {isLister ? 'YOUR NOTE' : 'THEIR NOTE'}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{req.response_note}</div>
          </div>
        )}

        {req.sale_id && (
          <>
            <Button block onClick={() => nav(`/sales/${req.sale_id}`)} style={{ height: 44, fontWeight: 600 }}>
              Open sale {req.sale_ref || ''}
            </Button>
            {/* The paperwork the co-share now needs. Two RENs on one fee is exactly
                the case the internal co-share form exists for, and this is the
                moment it is owed — the split was just agreed twice over, here and
                on the sale. The form itself is raised from the sale's Attachments,
                which is where it lives and where both agents will look for it. */}
            <div
              onClick={() => nav(`/sales/${req.sale_id}?tab=attachments`)}
              style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Internal co-share form</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, marginTop: 2 }}>
                  Put the split on paper both of you sign. Generated from the sale — nothing to fill in.
                </div>
              </div>
              <span style={{ color: 'var(--muted)' }}>›</span>
            </div>
          </>
        )}

        {/* Ending the request is deliberate and rare, so it sits at the bottom of the
            scroll rather than beside the primary action. */}
        {req.can_withdraw && !req.can_respond && (
          <span
            onClick={withdraw}
            style={{ alignSelf: 'center', fontSize: 13, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer', padding: '8px 4px' }}
          >
            {isLister ? 'Cancel this co-share' : 'Withdraw my request'}
          </span>
        )}

        <div style={{ height: 8 }} />
      </div>

      {/* The lister's two verbs, side by side while it is still waiting on them. */}
      {req.can_respond && (
        <div style={{ flex: 'none', display: 'flex', gap: 10, padding: '14px 16px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <Button block danger onClick={reject} style={{ height: 46, fontWeight: 600 }}>Decline</Button>
          <Button data-tour="cs-accept" block type="primary" onClick={() => setAcceptOpen(true)} style={{ height: 46, fontWeight: 600 }}>Accept</Button>
        </div>
      )}

      {req.can_create_sale && (
        <div style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <AsyncButton
            data-tour="cs-create-sale"
            type="primary"
            block
            size="large"
            onClick={createSale}
            onError={(e) => message.error(e.message)}
            style={{ height: 46, fontWeight: 600 }}
          >
            Create sale from this co-share
          </AsyncButton>
        </div>
      )}

      <AcceptSheet
        open={acceptOpen}
        request={req}
        onSubmit={accept}
        onError={(e) => message.error(e.message)}
        onClose={() => setAcceptOpen(false)}
      />
    </div>
  );
}

/**
 * Accepting is where the split is actually settled, so it is a sheet and not a
 * one-tap yes: the requester proposed a number, and this is the lister's chance to
 * say a different one before it becomes the sale's agent split.
 */
function AcceptSheet({ open, request, onSubmit, onError, onClose }) {
  const { mounted, closing } = useSheetAnim(open);
  const [agreed, setAgreed] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Raised by the agent's typing only — the seed below sets the fields directly.
  const [dirty, setDirty] = useState(false);
  const guard = useUnsavedChanges(open && dirty, { what: 'your acceptance' });
  const close = () => guard.confirmClose(onClose);

  // Open on what they asked for — the common answer is "yes, that's fine".
  useEffect(() => {
    if (!open) return;
    const proposed = request?.proposed_split_percentage;
    setAgreed(proposed != null ? String(proposed) : '50');
    setNote('');
    setDirty(false);
  }, [open, request]);

  if (!mounted) return null;

  const n = agreed === '' ? null : Number(agreed);
  const bad = agreed !== '' && (!Number.isFinite(n) || n < 0 || n > 100);
  const requesterName = request?.requester?.name || 'The requesting agent';

  const send = async () => {
    if (bad || busy) return;
    setBusy(true);
    try {
      await onSubmit({ agreed, note: note.trim() });
    } catch (e) {
      onError?.(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={close} className={`sheet-scrim${closing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className={`sheet-panel${closing ? ' is-closing' : ''}`} style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Accept co-share</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
            {requesterName} · {request?.listing?.listing_ref}
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>THEIR SHARE OF THE SPLIT</div>
            <Input
              data-tour="cs-split"
              value={agreed}
              onChange={(e) => { setDirty(true); setAgreed(e.target.value.replace(/[^\d.]/g, '')); }}
              inputMode="decimal"
              suffix="%"
              status={bad ? 'error' : undefined}
              style={{ height: 'var(--field-h)' }}
            />
            <div style={{ fontSize: 11.5, color: bad ? 'var(--bad)' : 'var(--muted)', marginTop: 5, lineHeight: 1.45 }}>
              {bad
                ? 'Enter a percentage between 0 and 100.'
                : `${requesterName} asked for ${request?.proposed_split_percentage != null ? `${request.proposed_split_percentage}%` : 'no particular share'}. You keep ${Number.isFinite(n) ? Number((100 - n).toFixed(2)) : '…'}%.`}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>NOTE (OPTIONAL)</div>
            <Input.TextArea
              value={note}
              onChange={(e) => { setDirty(true); setNote(e.target.value); }}
              maxLength={1000}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="Viewing on Saturday 3pm — bring the buyer…"
            />
          </div>

          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 15px', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55 }}>
            Accepting does not create anything yet. When the deal closes you raise the sale from this request and {requesterName} is added to it at this split — you can still adjust the figures on the sale form.
          </div>
        </div>

        <div style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
          <Button block onClick={close} style={{ height: 44 }}>Cancel</Button>
          <Button data-tour="cs-accept-submit" type="primary" block loading={busy} disabled={bad} onClick={send} style={{ height: 44, fontWeight: 600 }}>
            Accept co-share
          </Button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, mono, accent, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderBottom: last ? 'none' : '1px solid var(--border)', fontSize: 13 }}>
    <span style={{ color: 'var(--muted)', flex: 'none' }}>{k}</span>
    <span style={{ fontWeight: 600, textAlign: 'right', fontFamily: mono ? 'ui-monospace,monospace' : 'inherit', color: accent ? 'var(--primary)' : 'var(--text)' }}>{v}</span>
  </div>
);
